import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, merge, Observable, of, Subject, Subscription } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { LanguageService } from '@fcom/ui-translate';
import {
  ButtonPosition,
  ButtonTheme,
  ModalButtons,
  NotificationTheme,
  TabLayoutType,
  TabsComponent,
} from '@fcom/ui-components';
import {
  ElementActions,
  ElementTypes,
  FlightSegment,
  FragmentService,
  LoaderType,
  MediaQueryService,
  PageMetaService,
  ScrollService,
  ToasterService,
  TripType,
} from '@fcom/common';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { CmsDataService, ConfigService } from '@fcom/core/services';
import { equals, GlobalBookingTravelClass, isDeepEqual, NativeBridgeService, SentryLogger } from '@fcom/core';
import { Amount, BoundType, LocationInfoMap, PaxAmount } from '@fcom/dapi';
import { finShare, snapshot } from '@fcom/rx';
import { isPresent, isUndefined, LocalDate, stopPropagation, unsubscribe } from '@fcom/core/utils';
import {
  Cabin,
  FareFamily,
  FareInformation,
  FinnairBoundFareFamily,
  FinnairCabinClass,
  FinnairItineraryItemFlight,
  ItineraryItemFlight,
} from '@fcom/dapi/api/models';
import {
  FinnairMultiCitySearchInterface,
  GaContext,
  GtmEvent,
  GtmFlightSelectionData,
  GtmPurchaseFlow,
  MultivariateTestId,
  TestVariant,
} from '@fcom/common/interfaces';
import {
  DimensionsAndDetails,
  ExtendedFareFamily,
  FinnairBoundItemWithLocation,
  SortBy,
  UpgradeOfferActions,
} from '@fcom/common-booking';
import { CmsArticle } from '@fcom/core-api/';
import {
  GlobalBookingActions,
  globalBookingFlights,
  globalBookingPaxAmount,
  globalBookingSelections,
} from '@fcom/common/store';
import { profileOrNot, pseudoLocal } from '@fcom/core/selectors';
import { LoginStep, ProfileTier } from '@fcom/core-api/login';
import {
  getCabinClassShortCode,
  getTripTypeFromGlobalBookingFlights,
  isFlight,
} from '@fcom/common-booking/utils/common-booking.utils';
import { UpgradeOffer } from '@fcom/common-booking/interfaces/upgrade-offer.interface';
import { LoginActions, PseudoLocalActions } from '@fcom/core/actions';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import {
  AirOffersStatus,
  AirOffersStatusToText,
  BookingAndAppState,
  FinnairAirBounds,
  FinnairBoundGroupWithLocation,
  SelectionPhase,
} from '@fcom/common/interfaces/booking';
import { isShortHaulFlight } from '@fcom/common/utils';
import { ExpanderStatus } from '@fcom/booking-widget/interfaces/booking-widget.interface';
import { GLOBAL_BOOKING_WIDGET_EXPANDER_KEY } from '@fcom/booking-widget/constants';
import { BookingWidgetService } from '@fcom/booking-widget/services/booking-widget.service';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';

import { DimensionsAndDetailsModalService } from '../services/dimensions-and-details-modal.service';
import { CountryCode } from '../../../utils';
import { BoundsActions } from '../../../store/actions';
import {
  airBoundsLastRequestDepartureDate,
  airBoundsLastRequestReturnDate,
  airBoundsRequest,
  boundsCurrency,
  boundSelectionsDone,
  boundsHash,
  boundsStatus,
  boundsTotalPrice,
  fareFamiliesBounds,
  filteredInbounds,
  filteredOutbounds,
  flightSelectionGtmDataForAirBound,
  hasZeroBaggageOnEitherBound,
  inbounds,
  isOneWayBounds,
  lastRequestParams,
  outbounds,
  selectedAirBoundIds,
  selectedAirBoundsTicketSortBy,
  selectedInboundAirBound,
  selectedInboundAirBoundId,
  selectedInboundBound,
  selectedInboundFareFamilyBenefits,
  selectedInboundItinerary,
  selectedOutboundAirBound,
  selectedOutboundAirBoundId,
  selectedOutboundBound,
  selectedOutboundFareFamilyBenefits,
  selectedOutboundItinerary,
} from '../../../store/selectors';
import { BookingQueueService, BookingService } from '../../../services';
import { BookingGtmService } from '../../../services/booking-gtm.service';
import { LocationNames } from '../interfaces';
import { BookingAirBoundsService, TicketSelectionParams } from '../services';
import { containsOnlyOneCabin, getHighestCabinClass } from '../utils/offer.utils';
import { resetOutboundCache } from '../../../store/actions/bounds.actions';

export const offerListSortMap: { [id: string]: string } = Object.freeze({
  [SortBy.DURATION]: 'default',
  [SortBy.PRICE]: 'price',
  [SortBy.ARRIVAL]: 'arrivalTime',
  [SortBy.DEPARTURE]: 'departureTime',
});

const matchesCampaignFareFamily = (ffCode: string) => ffCode.includes('CAMP');

interface ItineraryAndUpgradesModalState {
  open: boolean;
  data: BoundInfos;
}

interface DimensionsAndDetailsModalState {
  open: boolean;
  data: DimensionsAndDetails;
}

interface ContinueModalState {
  open: boolean;
  data: ContinueModalData;
}

interface ContinueModalV2State {
  open: boolean;
  data: {
    contentUrlTranslationKey?: string;
  };
}

interface PromoCodeModalState {
  open: boolean;
  title: string;
  terms: string;
  promoCode: string;
}

type BoundInfos = {
  outbound?: FinnairBoundItemWithLocation;
  inbound?: FinnairBoundItemWithLocation;
  availableClasses?: { [segmentId: string]: FinnairCabinClass[] };
};

type ContinueModalData = {
  /**
   * Whether to show the zeroBaggage warning for the ticket type
   */
  zeroBaggage: boolean;
  contentUrlTranslationKey?: string;
};

const fragmentKey = 'fragments.multiCityError.url';

@Component({
  selector: 'fin-air-bounds-booking-ticket-selection-view',
  templateUrl: './air-bounds-booking-ticket-selection-view.component.html',
  styleUrls: ['./air-bounds-booking-ticket-selection-view.component.scss'],
  standalone: false,
})
export class AirBoundsBookingTicketSelectionViewComponent implements OnDestroy, OnInit {
  readonly AirOffersStatus = AirOffersStatus;
  readonly BoundType = BoundType;
  readonly SelectionPhase = SelectionPhase;
  readonly ModalButtons = ModalButtons;
  readonly LoaderType = LoaderType;
  readonly ButtonPosition = ButtonPosition;
  readonly NotificationTheme = NotificationTheme;
  readonly ButtonTheme = ButtonTheme;
  readonly TabLayoutType = TabLayoutType;
  readonly SortBy = SortBy;

  outbounds$: Observable<FinnairAirBounds>;
  inbounds$: Observable<FinnairAirBounds>;
  currencyCode$: Observable<string>;
  boundsStatus$: Observable<AirOffersStatus>;
  departureDate$: Observable<LocalDate>;
  returnDate$: Observable<LocalDate>;
  isOneWayBounds$: Observable<boolean>;
  selectedOutbound$: Observable<FinnairBoundGroupWithLocation>;
  selectedInbound$: Observable<FinnairBoundGroupWithLocation>;
  selectedOutboundAirBoundId$: Observable<string>;
  selectedOutboundAirBoundIdValue: string;
  selectedInboundAirBoundId$: Observable<string>;
  selectedInboundAirBoundIdValue: string;
  selectedOutboundFareFamilyBenefits$: Observable<FareFamily>;
  selectedInboundFareFamilyBenefits$: Observable<FareFamily>;
  selectedOutboundItinerary$: Observable<FinnairItineraryItemFlight[]>;
  selectedInboundItinerary$: Observable<FinnairItineraryItemFlight[]>;
  paxAmount$: Observable<PaxAmount>;
  hasFlightsInTheUnitedStates$: Observable<boolean>;
  fareFamilies$: Observable<FinnairAirBounds['fareFamilies']>;
  tripLocations$: Observable<LocationNames[]>;

  /**
   * Whether or not the bounds have been selected (both for two-way and the outbound for one-way trips.)
   */
  boundsSelected$: Observable<boolean>;
  locations$: Observable<LocationInfoMap>;
  showKoreaNotification$: Observable<boolean>;
  SEATS_LEFT_LIMIT = 9;
  selectedBoundsPrice$: Observable<Amount>;
  TripType = TripType;

  itineraryAndUpgradesModal: ItineraryAndUpgradesModalState = {
    open: false,
    data: {},
  };

  dimensionsAndDetailsModal: DimensionsAndDetailsModalState = {
    open: false,
    data: null,
  };

  continueModal: ContinueModalState = {
    open: false,
    data: null,
  };

  continueModalV2: ContinueModalV2State = {
    open: false,
    data: null,
  };

  promoCodeModal$: Observable<PromoCodeModalState> = of({
    open: false,
    title: null,
    terms: null,
    promoCode: null,
  });

  // == SELECTION PHASE ==
  selectionPhase$: BehaviorSubject<SelectionPhase> = new BehaviorSubject<SelectionPhase>(SelectionPhase.OUTBOUND);
  continueEnabled$: Observable<boolean>;
  continueButtonClicks$: Subject<boolean> = new Subject<boolean>();

  // == SORTING ==
  sortByPriceOrDuration$: Observable<SortBy>;
  inboundIdSortOrder$: Observable<string[]>;
  outboundIdSortOrder$: Observable<string[]>;
  isShortHaulFlight$: Observable<boolean>;

  @Input()
  goToOutboundPhase$: Observable<void>;

  @Output()
  refetchOffers: EventEmitter<void> = new EventEmitter();

  @Output()
  selectionPhaseChange: EventEmitter<SelectionPhase> = new EventEmitter();

  @ViewChild('tabs', { static: false })
  tabs: TabsComponent;

  promoCode$: Observable<string>;
  multicityErrorNotification$: Observable<CmsArticle>;
  tripType$: Observable<TripType>;
  upgradeTabSelected$: Observable<boolean>;
  boundsSortBy$: Observable<SortBy>;

  activeTab$ = new Subject<number>();
  private openBoundClick$ = new Subject<{ bound: FinnairBoundGroupWithLocation; boundType: BoundType }>();
  private subscription = new Subscription();

  upgradeAvailabilityEnabled = false;
  enableTicketTypeUpselling = false;
  isLoading: boolean;
  enableNewLanguageSelector: boolean;
  showOutboundFilters$: Observable<boolean>;
  showInboundFilters$: Observable<boolean>;
  enableNewStepper$: Observable<boolean>;

  // This is actuallly the variable that knows which sorting tab has been selected
  // But since the information is not needed/used outside this component,this hack
  // of this component knowing the truth, and the var (sortByPriceOrDuration$)
  // that comes from the bounds state via selector showing only part-truth, has been ok
  selectedSortCriteria$ = of(SortBy.BEST);
  shouldReload: boolean = true;
  activeCabinClassNotificationIds: string[] = [];
  showContinueButton$ = of(true);

  constructor(
    private router: Router,
    private bookingQueueService: BookingQueueService,
    private airBoundsService: BookingAirBoundsService,
    private languageService: LanguageService,
    private store$: Store<BookingAndAppState>,
    private gtmService: GtmService,
    private bookingGtmService: BookingGtmService,
    private bookingService: BookingService,
    private scrollService: ScrollService,
    private toasterService: ToasterService,
    private cmsDataService: CmsDataService,
    private fragmentService: FragmentService,
    private configService: ConfigService,
    @Inject(PLATFORM_ID) private platform: object,
    private cdr: ChangeDetectorRef,
    private dimensionsAndDetailsService: DimensionsAndDetailsModalService,
    private activatedRoute: ActivatedRoute,
    private pageMetaService: PageMetaService,
    private sentryLogger: SentryLogger,
    private nativeBridgeService: NativeBridgeService,
    private bookingWidgetService: BookingWidgetService,
    private mediaQueryService: MediaQueryService,
    private multivariateTestService: MultivariateTestService
  ) {}

  ngOnInit(): void {
    this.store$.dispatch(resetOutboundCache());

    this.subscription.add(
      this.store$.pipe(outbounds(), filter(Boolean)).subscribe(() => {
        this.shouldReload = false;
      })
    );
    this.subscription.add(
      this.store$.pipe(inbounds(), filter(Boolean)).subscribe(() => {
        this.shouldReload = false;
      })
    );

    this.subscription.add(
      this.goToOutboundPhase$?.subscribe(() => {
        const selectionPhase = this.selectionPhase$.getValue();
        if (selectionPhase === SelectionPhase.INBOUND) {
          this.selectionPhase$.next(SelectionPhase.OUTBOUND);
        }
      })
    );

    this.subscription.add(
      this.selectionPhase$.pipe(distinctUntilChanged()).subscribe((selectionPhase) => {
        this.selectionPhaseChange.emit(selectionPhase);
      })
    );

    this.upgradeAvailabilityEnabled = this.configService.cfg.enableUpgradeAvailabilityInBooking;
    this.enableTicketTypeUpselling = this.configService.cfg.enableTicketTypeUpselling;
    this.upgradeTabSelected$ = this.activeTab$.pipe(
      distinctUntilChanged(),
      map((index) => index === 1),
      tap(
        (upgradeTabOpen) =>
          upgradeTabOpen &&
          this.gtmService.trackElement(
            'upgrade-availability::open_modal',
            GaContext.BOOKING_FLOW,
            ElementTypes.MODAL,
            undefined,
            ElementActions.VIEW
          )
      )
    );

    this.enableNewLanguageSelector = !this.nativeBridgeService.isInsideNativeWebView;

    this.enableNewStepper$ = this.configService.cfg.enableNewStepper
      ? this.multivariateTestService.getTestVariant(MultivariateTestId.NEW_BOOKING_FLOW_STEPPER).pipe(
          map((variant) => variant.variant === TestVariant.B),
          finShare()
        )
      : of(false);

    const outboundBase$ = this.store$.pipe(filteredOutbounds(), filter(Boolean), finShare());
    const inboundBase$ = this.store$.pipe(filteredInbounds(), filter(Boolean), finShare());

    const hasSpecialOutbounds$: Observable<boolean> = outboundBase$.pipe(
      map((bounds) => Object.keys(bounds.fareFamilies).some((ffCode) => matchesCampaignFareFamily(ffCode))),
      finShare()
    );

    const hasSpecialInbounds$: Observable<boolean> = inboundBase$.pipe(
      map((bounds) => Object.keys(bounds.fareFamilies).some((ffCode) => matchesCampaignFareFamily(ffCode))),
      finShare()
    );

    this.outbounds$ = combineLatest([outboundBase$, hasSpecialOutbounds$]).pipe(
      map(([bounds, hasSpecial]) => (hasSpecial ? this.mapSpecialOffersStatusToBounds(bounds) : bounds)),
      finShare()
    );

    this.inbounds$ = combineLatest([inboundBase$, hasSpecialInbounds$]).pipe(
      map(([bounds, hasSpecial]) => (hasSpecial ? this.mapSpecialOffersStatusToBounds(bounds) : bounds)),
      finShare()
    );

    this.showKoreaNotification$ = this.outbounds$.pipe(
      map((bounds) => {
        const location = bounds.locations[bounds.request.itineraries[0].departureLocationCode];
        return location && location.countryCode === CountryCode.KOREA;
      }),
      finShare()
    );
    this.showOutboundFilters$ = this.store$.pipe(
      outbounds(),
      filter(Boolean),
      map((bounds) => bounds.boundGroups.length > 3),
      finShare()
    );
    this.showInboundFilters$ = this.store$.pipe(
      inbounds(),
      filter(Boolean),
      map((bounds) => bounds.boundGroups.length > 3),
      finShare()
    );
    this.multicityErrorNotification$ = this.fragmentService.getFragment<CmsArticle>(fragmentKey);
    this.currencyCode$ = this.store$.pipe(boundsCurrency());
    this.boundsStatus$ = this.store$.pipe(boundsStatus());
    this.isOneWayBounds$ = this.store$.pipe(isOneWayBounds());
    this.departureDate$ = this.store$.pipe(airBoundsLastRequestDepartureDate());
    this.returnDate$ = this.store$.pipe(airBoundsLastRequestReturnDate());
    this.fareFamilies$ = this.store$.pipe(fareFamiliesBounds());

    this.tripLocations$ = this.boundsStatus$.pipe(
      withLatestFrom(this.store$.pipe(globalBookingFlights())),
      map(([_, locations]) =>
        locations.map(({ destination, origin }) => ({
          destination: destination.cityName,
          origin: origin.cityName,
        }))
      )
    );

    this.tripType$ = this.store$
      .pipe(globalBookingFlights())
      .pipe(map((flights) => getTripTypeFromGlobalBookingFlights(flights)));

    this.hasFlightsInTheUnitedStates$ = combineLatest([this.outbounds$, this.inbounds$]).pipe(
      map(
        ([out, inb]) =>
          (isPresent(out) && out.hasFlightsInUnitedStates) || (isPresent(inb) && inb.hasFlightsInUnitedStates)
      ),
      finShare()
    );

    //check
    this.subscription.add(
      this.boundsStatus$.pipe(distinctUntilChanged()).subscribe((status) => {
        if (status === AirOffersStatus.PENDING) {
          this.selectionPhase$.next(SelectionPhase.OUTBOUND);
          this.shouldReload = true;
        }
      })
    );
    this.subscription.add(
      this.boundsStatus$
        .pipe(
          filter((s) => s === AirOffersStatus.ERROR || s === AirOffersStatus.NO_FLIGHTS_FOUND),
          distinctUntilChanged()
        )
        .subscribe(() => {
          // Reset the phase to Outbound in case of error
          this.selectionPhase$.next(SelectionPhase.OUTBOUND);
        })
    );
    this.subscription.add(
      this.selectionPhase$.subscribe((selectionPhase) => {
        if (selectionPhase === SelectionPhase.INBOUND) {
          this.shouldReload = true;
        }
      })
    );
    this.boundsSelected$ = this.store$.pipe(boundSelectionsDone());

    // the bounds that have boundFareFamilies
    this.selectedOutbound$ = this.store$.pipe(selectedOutboundBound());
    this.selectedInbound$ = this.store$.pipe(selectedInboundBound());

    this.selectedOutboundAirBoundId$ = this.store$.pipe(selectedOutboundAirBoundId());
    this.selectedInboundAirBoundId$ = this.store$.pipe(selectedInboundAirBoundId());

    this.selectedOutboundFareFamilyBenefits$ = this.store$.pipe(selectedOutboundFareFamilyBenefits());
    this.selectedInboundFareFamilyBenefits$ = this.store$.pipe(selectedInboundFareFamilyBenefits());
    this.selectedOutboundItinerary$ = this.store$.pipe(selectedOutboundItinerary());
    this.selectedInboundItinerary$ = this.store$.pipe(selectedInboundItinerary());

    this.locations$ = combineLatest([this.outbounds$, this.inbounds$.pipe(startWith(null))]).pipe(
      map(([outbounds, inbounds]) => ({
        ...outbounds.locations,
        ...inbounds?.locations,
      })),
      finShare()
    );

    this.paxAmount$ = this.outbounds$.pipe(
      map(
        ({
          request: {
            travelers: { adults = 0, c15s = 0, children = 0, infants = 0 },
          },
        }) => ({
          adults,
          c15s,
          children,
          infants,
        })
      )
    );

    // if we have original prices, the promotion code has been applied
    const outboundHasDiscount$ = this.outbounds$.pipe(
      map((bounds) => bounds?.boundGroups.some((group) => group.fareFamilies.some((ff) => ff.originalPrice)))
    );

    const promoCode$: Observable<string | null> = this.store$.pipe(
      lastRequestParams(),
      filter(Boolean),
      map((params: TicketSelectionParams) => params?.promoCode || null)
    );

    this.promoCode$ = combineLatest([promoCode$, outboundHasDiscount$]).pipe(
      filter(([promoCode, hasDiscount]) => isPresent(promoCode) && !!hasDiscount),
      map(([promoCode]) => promoCode),
      distinctUntilChanged()
    );

    this.isShortHaulFlight$ = this.outbounds$.pipe(
      map((bounds) =>
        bounds.request.itineraries.every((i) => {
          const departureLocation = bounds.locations[i.departureLocationCode];
          const destinationLocation = bounds.locations[i.destinationLocationCode];
          return isShortHaulFlight(departureLocation, destinationLocation);
        })
      )
    );

    this.subscription.add(
      this.promoCode$.subscribe((promoCode) => {
        this.trackPromoCodeEvent(promoCode, true);
      })
    );

    this.subscription.add(
      this.selectedOutboundAirBoundId$.subscribe((id) => {
        this.selectedOutboundAirBoundIdValue = id;
      })
    );

    this.subscription.add(
      this.selectedInboundAirBoundId$.subscribe((id) => {
        this.selectedInboundAirBoundIdValue = id;
      })
    );

    this.promoCodeModal$ = this.boundsStatus$.pipe(
      filter((s) => s !== AirOffersStatus.PENDING && s !== AirOffersStatus.INITIAL),
      withLatestFrom(promoCode$, outboundHasDiscount$),
      filter(([, promoCode, hasDiscount]) => isPresent(promoCode) && !hasDiscount),
      switchMap(([, promoCode]) => {
        this.trackPromoCodeEvent(promoCode, false);
        return this.cmsDataService
          .getPromoCodeTerms(promoCode, this.languageService.langValue, this.languageService.localeValue)
          .pipe(
            map((terms) => {
              return {
                open: !!terms,
                terms: terms?.description,
                title: terms?.title,
                promoCode: promoCode,
              };
            })
          );
      }),
      finShare()
    );

    // == SORTING ==
    // This is supposed to be the variable that tells which sort tab is selected
    // but actually it can be only SortBy.PRICE or SortBy.DURATION, so therefore the name
    this.sortByPriceOrDuration$ = this.store$.pipe(selectedAirBoundsTicketSortBy());
    this.outboundIdSortOrder$ = combineLatest([this.outbounds$, this.sortByPriceOrDuration$]).pipe(
      filter(([o]) => !!o?.sorting?.boundGroups),
      map(([bounds, sortBy]): string[] => bounds.sorting.boundGroups[offerListSortMap[sortBy]])
    );
    this.inboundIdSortOrder$ = combineLatest([this.inbounds$, this.sortByPriceOrDuration$]).pipe(
      filter(([i]) => !!i?.sorting?.boundGroups),
      map(([bounds, sortBy]): string[] => bounds.sorting.boundGroups[offerListSortMap[sortBy]])
    );

    // Scroll to top and remove toaster messages on every phase change
    this.subscription.add(
      this.selectionPhase$.pipe(filter(() => isPlatformBrowser(this.platform))).subscribe(() => {
        this.scrollService.scrollTop();
        if (this.activeCabinClassNotificationIds.length) {
          this.activeCabinClassNotificationIds.forEach((id) => this.toasterService.removeMessage(id));
          this.activeCabinClassNotificationIds = [];
        }
      })
    );

    this.subscription.add(
      this.selectionPhase$
        .pipe(
          switchMap((selectionPhase) =>
            this.store$.pipe(
              selectionPhase === SelectionPhase.OUTBOUND ? selectedOutboundAirBound() : selectedInboundAirBound()
            )
          ),
          filter(Boolean),
          filter(({ fareInformation }) => !containsOnlyOneCabin(fareInformation)),
          distinctUntilChanged((fare1, fare2) => fare1.id === fare2.id),
          withLatestFrom(
            this.selectionPhase$.pipe(
              switchMap((selectionPhase) => {
                return selectionPhase === SelectionPhase.OUTBOUND ? this.selectedOutbound$ : this.selectedInbound$;
              })
            ),
            this.locations$
          ),
          switchMap(([selectedFareFamily, selectedBound, locations]) => {
            const { fareInformation } = selectedFareFamily;
            const businessFaultySegments =
              !containsOnlyOneCabin(fareInformation, GlobalBookingTravelClass.BUSINESS) &&
              fareInformation.filter(({ cabinClass }) => cabinClass !== GlobalBookingTravelClass.BUSINESS);

            const ecoPremiumFaultySegments =
              !containsOnlyOneCabin(fareInformation, GlobalBookingTravelClass.ECOPREMIUM) &&
              fareInformation.filter(({ cabinClass }) => cabinClass !== GlobalBookingTravelClass.ECOPREMIUM);

            const faultySegments =
              isPresent(businessFaultySegments) && businessFaultySegments.length === 1
                ? businessFaultySegments
                : isPresent(ecoPremiumFaultySegments) && ecoPremiumFaultySegments.length === 1
                  ? ecoPremiumFaultySegments
                  : undefined;

            const params: LocationNames = {};
            if (faultySegments) {
              // find the origin and destination for the faulty segment.
              const [fs] = faultySegments;
              const [segment] = selectedBound.details.itinerary
                .filter(isFlight)
                .filter((seg) => seg.id === fs.segmentId);

              params.origin = locations[segment.departure.locationCode].cityName;
              params.destination = locations[segment.arrival.locationCode].cityName;
            }

            return combineLatest([
              this.selectedOutbound$,
              this.selectedInbound$,
              this.languageService.translate(
                businessFaultySegments.length === 1
                  ? 'ticketSelection.differentCabinClassWarningNew'
                  : ecoPremiumFaultySegments.length === 1
                    ? 'ticketSelection.differentCabinClassPremiumEconomyWarningNew'
                    : 'ticketSelection.differentCabinClassComplexWarning',
                params
              ),
              this.languageService.translate('ticketSelection.differentCabinClassWarningLink.linkText'),
              of(selectedFareFamily),
            ]);
          }),
          distinctUntilChanged((previous, next) => previous[4]?.id === next[4]?.id),
          finShare()
        )
        .subscribe(([selectedOutbound, selectedInbound, notificationText, linkText]) => {
          const selectionPhase = this.selectionPhase$.getValue();

          if (this.activeCabinClassNotificationIds.length) {
            this.activeCabinClassNotificationIds.forEach((id) => this.toasterService.removeMessage(id));
          }

          const id = uuid();

          const delay = this.activeCabinClassNotificationIds.length > 0;

          this.activeCabinClassNotificationIds = [id];

          this.toasterService.addMessageToQueue({
            id,
            content: notificationText,
            theme: NotificationTheme.WARNING,
            target: {
              linkText: linkText,
              callback: () => {
                this.showDetailsModal(
                  selectionPhase === SelectionPhase.OUTBOUND ? selectedOutbound?.details : null,
                  selectionPhase === SelectionPhase.INBOUND ? selectedInbound?.details : null,
                  selectionPhase,
                  selectionPhase === SelectionPhase.OUTBOUND
                    ? selectedOutbound?.fareFamilies
                    : selectedInbound?.fareFamilies
                );
                this.activeCabinClassNotificationIds = this.activeCabinClassNotificationIds.filter(
                  (storedId) => storedId !== id
                );
              },
            },
            keepMessageAfterNavigation: false,
            delay,
          });
        })
    );

    /**
     * gtm error event based on boundsStatus error
     */
    this.subscription.add(
      combineLatest([this.boundsStatus$, this.store$.pipe(globalBookingSelections())])
        .pipe(
          filter(
            ([boundsStatus]) =>
              boundsStatus === AirOffersStatus.ERROR ||
              boundsStatus === AirOffersStatus.INVALID_INPUT ||
              boundsStatus === AirOffersStatus.NO_FLIGHTS_FOUND
          ),
          distinctUntilChanged((prev, next) => prev[0] === next[0] && isDeepEqual(prev[1], next[1]))
        )
        .subscribe(([boundsStatus, selection]) => {
          const isValidSelection = selection.flights.some((f) => isPresent(f?.origin) && isPresent(f?.destination));
          const flightsToFlightSearchParams: FlightSegment[] = isValidSelection
            ? selection.flights.map((f) => ({
                origin: f.origin?.locationCode,
                destination: f.destination.locationCode,
                departureDate: f.departureDate,
              }))
            : [];

          this.gtmService.errorEvent(AirOffersStatusToText[boundsStatus], flightsToFlightSearchParams);
        })
    );

    this.setUpContinueButton();
    this.setUpGtmEventTracking();
    this.setUpAlternativeSegmentNotifier();
    this.handleBoundsPreSelection();
    this.setupUpgradeOffer();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
    this.store$.dispatch(resetOutboundCache());
    this.store$.dispatch(BoundsActions.reset());
  }

  trackPromoCodeEvent(promoCode: string, isApplied: boolean): void {
    this.gtmService.trackElement(
      `promo-code_${isApplied ? 'applied' : 'not-applied'}`,
      GaContext.FLIGHT_SELECTION,
      ElementTypes.NOTIFICATION,
      promoCode,
      ElementActions.VIEW
    );
  }

  handleBoundsPreSelection(): void {
    const selectedOutboundAirBound$ = this.store$.pipe(selectedOutboundAirBound());
    const queryParams = this.airBoundsService.getLastRequestParams();

    if (queryParams.departureFlightNumbers) {
      // if outbound flight is selected, but inbound is not selected
      this.subscription.add(
        selectedOutboundAirBound$
          .pipe(filter(Boolean), take(1), delay(0), withLatestFrom(this.isOneWayBounds$, this.isShortHaulFlight$))
          .subscribe(([outbound, isOneWay, isShortHaul]: [FinnairBoundFareFamily, boolean, boolean]) => {
            if (isOneWay) {
              this.createCart(isShortHaul);
              this.continueToNextStep();
            } else {
              this.airBoundsService.getReturnBounds(outbound.id, outbound.fareFamilyCode);
            }
          })
      );

      // if both outbound and inbound flights are selected
      this.subscription.add(
        combineLatest([this.selectedOutboundAirBoundId$, this.selectedInboundAirBoundId$])
          .pipe(
            filter(([outboundId, inboundId]) => !isUndefined(outboundId) && !isUndefined(inboundId)),
            take(1),
            switchMap(() => this.isShortHaulFlight$)
          )
          .subscribe((isShortHaul) => {
            this.createCart(isShortHaul);
            this.bookingService.setStep({ step: BOOKING_STEPS.PAXDETAILS });
          })
      );
    }
  }

  setUpGtmEventTracking(): void {
    this.subscription.add(
      this.openBoundClick$
        .pipe(
          switchMap(({ bound, boundType }: { bound: FinnairBoundGroupWithLocation; boundType: BoundType }) =>
            this.store$.pipe(
              globalBookingPaxAmount(),
              switchMap((paxAmount: PaxAmount) =>
                this.store$.pipe(flightSelectionGtmDataForAirBound(bound, boundType, paxAmount))
              ),
              take(1),
              withLatestFrom(this.store$.pipe(profileOrNot())),
              map(([gtmData, profile]) =>
                profile?.tier === ProfileTier.CORPORATE
                  ? gtmData.map((d) => ({ ...d, purchaseFlow: GtmPurchaseFlow.CORPORATE }))
                  : gtmData
              )
            )
          )
        )
        .subscribe((gtmFlightSelectionData: GtmFlightSelectionData[]) => {
          this.gtmService.flightSelection(GtmEvent.FLIGHT_SELECTION_VIEW, gtmFlightSelectionData);
        })
    );

    this.subscription.add(
      this.selectionPhase$
        .pipe(
          switchMap((phase) =>
            (phase === SelectionPhase.OUTBOUND ? this.outbounds$ : this.inbounds$).pipe(
              map((bound) =>
                bound.boundGroups.find(({ fareFamilies }) =>
                  fareFamilies.find((fare) => fare.quota < this.SEATS_LEFT_LIMIT)
                )
              )
            )
          ),
          map(isPresent)
        )
        .subscribe((isSeatsLeftShown) => {
          this.gtmService.trackElement(
            'seat-notification-shown',
            GaContext.FLIGHT_SELECTION,
            ElementTypes.LINK,
            isSeatsLeftShown.toString(),
            ElementActions.VIEW
          );
        })
    );

    this.subscription.add(
      this.outbounds$.pipe(distinctUntilChanged()).subscribe((outboundFlights) => {
        this.gtmService.trackElement(
          'outbound-flights-shown-revenue',
          GaContext.FLIGHT_SELECTION,
          ElementTypes.ACCORDION,
          outboundFlights.boundGroups.length.toString(),
          ElementActions.VIEW
        );
      })
    );

    this.subscription.add(
      this.inbounds$.pipe(distinctUntilChanged()).subscribe((inboundFlights) => {
        this.gtmService.trackElement(
          'inbound-flights-shown-revenue',
          GaContext.FLIGHT_SELECTION,
          ElementTypes.ACCORDION,
          inboundFlights.boundGroups.length.toString(),
          ElementActions.VIEW
        );
      })
    );
  }

  setUpContinueButton(): void {
    const selectedOutboundAirBound$ = this.store$.pipe(selectedOutboundAirBound());
    const selectedInboundAirBound$ = this.store$.pipe(selectedInboundAirBound());

    const currentQuota$ = this.selectionPhase$.pipe(
      switchMap((phase) => (phase === SelectionPhase.OUTBOUND ? selectedOutboundAirBound$ : selectedInboundAirBound$)),
      map((fareFamily) => fareFamily?.quota ?? this.SEATS_LEFT_LIMIT)
    );

    this.subscription.add(
      this.continueButtonClicks$
        .pipe(
          withLatestFrom(this.selectionPhase$),
          switchMap(([, phase]) =>
            this.store$.pipe(
              phase === SelectionPhase.OUTBOUND ? selectedOutboundAirBound() : selectedInboundAirBound(),
              filter((selectedFareFamily) => !!selectedFareFamily?.isCheapestOffer),
              map(() => phase)
            )
          )
        )
        .subscribe((selectionPhase) => {
          this.gtmService.trackElement(
            `${selectionPhase === SelectionPhase.OUTBOUND ? 'outbound' : 'inbound'}_cheapest-flight-selected`,
            GaContext.FLIGHT_SELECTION,
            ElementTypes.BUTTON,
            undefined,
            ElementActions.SUBMIT
          );
        })
    );

    this.subscription.add(
      this.continueButtonClicks$
        .pipe(
          withLatestFrom(currentQuota$),
          map(([, currentQuota]) => currentQuota),
          filter((currentQuota) => currentQuota < this.SEATS_LEFT_LIMIT)
        )
        .subscribe((currentQuota) => {
          this.gtmService.trackElement(
            'seat-notification-chosen',
            GaContext.FLIGHT_SELECTION,
            ElementTypes.BUTTON,
            currentQuota.toString(),
            ElementActions.CLICK
          );
        })
    );

    this.subscription.add(
      this.continueButtonClicks$
        .pipe(
          withLatestFrom(this.isOneWayBounds$, this.selectionPhase$, selectedOutboundAirBound$, this.isShortHaulFlight$)
        )
        .subscribe(this.respondToContinueClick())
    );

    this.subscription.add(
      this.continueButtonClicks$.subscribe(() => {
        this.bookingGtmService.sendFlightSelectionDataCloud();
      })
    );

    this.showContinueButton$ = combineLatest([
      this.store$.pipe(pseudoLocal(GLOBAL_BOOKING_WIDGET_EXPANDER_KEY)),
      this.mediaQueryService.isBreakpoint$('tablet_down'),
    ]).pipe(
      distinctUntilChanged((pre, next) => equals(pre, next)),
      map(
        ([status, isTabletDown]) =>
          status !== `${GLOBAL_BOOKING_WIDGET_EXPANDER_KEY}-${ExpanderStatus.OPEN}` || !status || !isTabletDown
      )
    );

    this.continueEnabled$ = this.selectionPhase$.pipe(
      switchMap((phase: SelectionPhase): Observable<boolean | string> => {
        if (phase === SelectionPhase.OUTBOUND) {
          return this.selectedOutboundAirBoundId$.pipe(startWith(undefined as string));
        }
        if (phase === SelectionPhase.INBOUND) {
          return this.selectedInboundAirBoundId$.pipe(startWith(undefined as string));
        }
        return of(true);
      }),
      map(isPresent),
      distinctUntilChanged()
    );

    this.selectedBoundsPrice$ = this.store$.pipe(boundsTotalPrice());
  }

  private respondToContinueClick(): (
    value: [boolean, boolean, SelectionPhase, FinnairBoundFareFamily, boolean]
  ) => void {
    return ([_, isOneWay, phase, { id, fareFamilyCode }, isShortHaulFlight]) => {
      const isAtOutboundSelection = phase === SelectionPhase.OUTBOUND;
      const shouldDisplayInboundView = isAtOutboundSelection && !isOneWay;

      if (shouldDisplayInboundView) {
        // Keep other filters, but reset time filters
        this.store$.dispatch(BoundsActions.resetArrivalTime());
        this.store$.dispatch(BoundsActions.resetDepartureTime());

        this.airBoundsService.getReturnBounds(id, fareFamilyCode);
        this.selectionPhase$.next(SelectionPhase.INBOUND);
        return;
      }

      this.createCart(isShortHaulFlight);
    };
  }

  setUpAlternativeSegmentNotifier(): void {
    this.subscription.add(
      merge(this.store$.pipe(selectedOutboundAirBound()), this.store$.pipe(selectedInboundAirBound()))
        .pipe(
          filter(Boolean),
          withLatestFrom(
            this.languageService.translate('ticketSelection.travelClassAlternative'),
            this.store$.pipe(airBoundsRequest())
          )
        )
        .subscribe(([{ fareInformation }, notificationText, { cabin }]) => {
          if (cabin !== Cabin.MIXED) {
            const hasTravelType = fareInformation.some(
              (fareInformation: FareInformation) => fareInformation.cabinClass === cabin
            );
            if (!hasTravelType) {
              this.toasterService.addMessageToQueue({
                id: 'travel-class-alternative',
                content: notificationText,
              });
            }
          }
        })
    );
  }

  setupUpgradeOffer(): void {
    if (!this.configService.cfg.enableUpgradeTravelClass) {
      return;
    }
    this.subscription.add(
      combineLatest([
        this.selectedOutbound$,
        this.selectedInbound$,
        this.selectedOutboundAirBoundId$,
        this.selectedInboundAirBoundId$,
        this.fareFamilies$,
        this.isOneWayBounds$,
      ])
        .pipe(
          filter(([_outbound, _inbound, outboundId, inboundId, _fareFamilies, isOneWay]) => {
            return isOneWay ? isPresent(outboundId) : isPresent(inboundId);
          })
        )
        .subscribe((params) => {
          this.triggerUpgradeOffer(params);
        })
    );
  }

  triggerUpgradeOffer(
    params: [
      FinnairBoundGroupWithLocation,
      FinnairBoundGroupWithLocation,
      string,
      string,
      FinnairAirBounds['fareFamilies'],
      boolean,
    ]
  ): void {
    const [outbound, inbound, selectedOutboundId, selectedInboundId, fareFamilies] = params;
    const selectedOutboundFareFamily = outbound.fareFamilies.find((ff) => ff.id === selectedOutboundId);
    const selectedInboundFareFamily = inbound?.fareFamilies.find((ff) => ff.id === selectedInboundId);
    const outboundUpgrade = this.getUpgradeFareFamily(selectedOutboundFareFamily, outbound);
    if (!outboundUpgrade?.upgradeFareFamily) {
      return;
    }

    const inboundUpgrade = selectedInboundFareFamily
      ? this.getUpgradeFareFamily(selectedInboundFareFamily, inbound)
      : null;

    const currentFareFamily = fareFamilies[selectedOutboundFareFamily.fareFamilyCode];
    const upgradeFareFamily = fareFamilies[outboundUpgrade.upgradeFareFamily.fareFamilyCode];

    if (!this.hasMatchingBrandName(!!inbound, outboundUpgrade, inboundUpgrade, fareFamilies)) {
      this.store$.dispatch(UpgradeOfferActions.clearUpgradeOffer());
      return;
    }

    const totalPrice = [outboundUpgrade, inboundUpgrade].reduce((acc: number, off: UpgradeOffer) => {
      const ff = off?.upgradeFareFamily;
      if (!ff?.price) {
        return acc;
      }
      return acc + parseFloat(ff.price);
    }, 0);
    const offer = {
      currentFareFamily,
      upgradeFareFamily,
      outboundId: outboundUpgrade.upgradeFareFamily.id,
      inboundId: inboundUpgrade?.upgradeFareFamily.id,
      timestamp: Date.now(),
      outboundPrice: selectedOutboundFareFamily.price,
      outboundUpgradePrice: outboundUpgrade.upgradeFareFamily.price,
      inboundPrice: selectedInboundFareFamily?.price,
      inboundUpgradePrice: inboundUpgrade?.upgradeFareFamily.price,
      totalUpgradePrice: totalPrice,
    };
    this.store$.dispatch(UpgradeOfferActions.setUpgradeOffer({ offer }));
  }

  hasMatchingBrandName(
    roundTrip: boolean,
    outboundUpgrade: UpgradeOffer,
    inboundUpgrade: UpgradeOffer,
    fareFamilies: FinnairAirBounds['fareFamilies']
  ): boolean {
    if (!roundTrip) {
      return true;
    }
    if (!inboundUpgrade) {
      return false;
    }
    const currentOutbound = fareFamilies[outboundUpgrade.currentFareFamily.fareFamilyCode];
    const currentInbound = fareFamilies[inboundUpgrade.currentFareFamily.fareFamilyCode];
    if (currentOutbound.brandName !== currentInbound.brandName) {
      return false;
    }

    const outboundNew = fareFamilies[outboundUpgrade.upgradeFareFamily.fareFamilyCode];
    const inboundNew = fareFamilies[inboundUpgrade.upgradeFareFamily.fareFamilyCode];
    return outboundNew.brandName === inboundNew.brandName;
  }

  /**
   * Returns fare family that is "one step" better than currently selected
   * fare family in current cabin class.
   * E.g.
   * Economy light -> Economy Classic
   * Economy flex -> null
   * Business classic -> Business flex
   * @param current
   * @param bound
   */
  getUpgradeFareFamily(current: FinnairBoundFareFamily, bound: FinnairBoundGroupWithLocation): UpgradeOffer | null {
    const cabinClass = getHighestCabinClass(current);
    const cabinClassShortCode = getCabinClassShortCode(cabinClass);

    /**
     * Fare family code can have optional A or S at the beginning (A for AJB bounds, S for SJB bounds),
     * next there is the cabin class code E / PE / B and after that there can be some campaign code before the actual
     * fare family code
     */
    let re: RegExp;
    const regExStart = `^[AS]{0,1}${cabinClassShortCode}.*`;
    if (current.fareFamilyCode.includes('LIGHT')) {
      re = new RegExp(regExStart + 'CLASI');
    } else if (current.fareFamilyCode.includes('CLASI')) {
      re = new RegExp(regExStart + 'FLEX');
    } else {
      return null;
    }
    const fareFamily = re ? bound.fareFamilies.find((ff) => re.test(ff.fareFamilyCode)) : null;
    if (!fareFamily) {
      return null;
    }
    return {
      currentFareFamily: current,
      upgradeFareFamily: fareFamily,
    };
  }

  backToDateSelection(): void {
    this.bookingService.setStep({ step: BOOKING_STEPS.ENTRY });
  }

  selectOutboundFareFamily(selectedFareFamily: { fareFamily: ExtendedFareFamily; checked: boolean }): void {
    this.store$.dispatch(
      BoundsActions.setOutboundAirBoundId({
        outboundAirBoundId: selectedFareFamily?.checked ? selectedFareFamily?.fareFamily?.id : null,
      })
    );

    this.updateDeeplinkOnFareFamilySelection(BoundType.outbound, selectedFareFamily?.fareFamily?.fareFamilyCode);
  }

  selectInboundFareFamily(selectedFareFamily: { fareFamily: ExtendedFareFamily; checked: boolean }): void {
    this.store$.dispatch(
      BoundsActions.setInboundAirBoundId({
        inboundAirBoundId: selectedFareFamily?.checked ? selectedFareFamily?.fareFamily?.id : null,
      })
    );

    this.updateDeeplinkOnFareFamilySelection(BoundType.inbound, selectedFareFamily?.fareFamily?.fareFamilyCode);
  }

  activateSelectionPhase(selectionPhase: SelectionPhase): void {
    this.selectionPhase$.next(selectionPhase);
  }

  onShowSpinner(): void {
    this.isLoading = true;

    setTimeout(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    }, 300);
  }

  selectSortBy(sortBy: SortBy): void {
    // We don't dispatch an action on best, because something somewhere makes a new BE call if we dispatch the SortBy.BEST
    // This also affects the naming of this.sortByPriceOrDuration$, because that var won't return SortBy.BEST
    if (sortBy !== SortBy.BEST) {
      this.store$.dispatch(BoundsActions.setSortBy({ sortBy }));
    }

    this.selectedSortCriteria$ = of(sortBy);
    this.onShowSpinner();
  }

  onContinueClick(event: Event): void {
    stopPropagation(event);
    // dispatch the original selection from booking widget service
    this.bookingWidgetService.setGlobalBookingSelectionToOriginal();
    this.continueButtonClicks$.next(true);
  }

  createCart(isShortHaulFlight: boolean): void {
    const bounds: string[] = snapshot(this.store$.pipe(selectedAirBoundIds()));
    // TODO: Validate that both bounds have been selected
    this.triggerCreateCart(bounds);
    const zeroBaggage: boolean = snapshot(this.store$.pipe(hasZeroBaggageOnEitherBound()));

    if (zeroBaggage && !this.enableTicketTypeUpselling) {
      const contentUrlTranslationKey =
        this.bookingService.getContentUrlTranslationKeySuperLightModalWindow(isShortHaulFlight);

      if (contentUrlTranslationKey) {
        this.openSuperLightWarningModal(contentUrlTranslationKey);
      }
    }
    if (!this.continueModalV2.open && !this.continueModal.open) {
      this.continueToNextStep();
    }
  }

  closeDimensionAndDetailsModal(): void {
    this.dimensionsAndDetailsService.closeModal();
    this.dimensionsAndDetailsModal = { open: false, data: null };
  }

  superLightWarningAnalytics(eventType: 'open' | 'close' | 'continue'): void {
    this.gtmService.trackElement(
      `baggage-superlight-modal-${eventType}`,
      'flight-selection',
      ElementTypes.LINK,
      'true',
      ElementActions.CLICK
    );
  }

  openSuperLightWarningModal(contentUrlTranslationKey: string): void {
    if (this.continueModalV2.open) {
      return;
    }
    this.continueModalV2 = {
      open: true,
      data: {
        contentUrlTranslationKey,
      },
    };
    this.superLightWarningAnalytics('open');
  }

  closeSuperLightWarningModal(): void {
    if (!this.continueModalV2.open) {
      return;
    }
    this.continueModalV2 = {
      open: false,
      data: null,
    };

    this.superLightWarningAnalytics('close');
  }

  continueSuperLightWarningModal(): void {
    if (!this.continueModalV2.open) {
      return;
    }
    this.continueModalV2 = {
      open: false,
      data: null,
    };

    this.superLightWarningAnalytics('continue');
    this.continueToNextStep();
  }

  closeModals(): void {
    this.itineraryAndUpgradesModal = { open: false, data: {} };
    this.continueModal = { open: false, data: null };
    this.continueModalV2 = { open: false, data: null };
    this.dimensionsAndDetailsModal = { open: false, data: null };
    this.promoCodeModal$ = of({ open: false, title: null, terms: null, promoCode: null });
    this.tabs.selectTab(this.tabs?.tabs[0]);
  }

  closeDiscountModal(): void {
    this.promoCodeModal$ = of({ open: false, title: null, terms: null, promoCode: null });
    this.store$.dispatch(GlobalBookingActions.setDiscountCode({ discountCode: null }));
  }

  continueToNextStep(): void {
    this.bookingService.setStep({ step: BOOKING_STEPS.PAXDETAILS });
  }

  showDetailsModal(
    outbound: FinnairBoundItemWithLocation,
    inbound: FinnairBoundItemWithLocation,
    selectionPhase: SelectionPhase,
    fareFamilies: FinnairBoundFareFamily[] = undefined
  ): void {
    const { cabin } = this.airBoundsService.getLastRequestParams();
    const availableClasses =
      fareFamilies && cabin === Cabin.MIXED ? this.getAvailableClasses(outbound || inbound, fareFamilies) : [];

    const outboundFareFamily = this.selectedOutboundAirBoundIdValue
      ? fareFamilies.find(({ id }) => id === this.selectedOutboundAirBoundIdValue)
      : null;
    const inboundFareFamily = this.selectedInboundAirBoundIdValue
      ? fareFamilies.find(({ id }) => id === this.selectedInboundAirBoundIdValue)
      : null;

    const selectedOutbound = snapshot(this.selectedOutbound$);
    const data = {
      outbound:
        selectionPhase === SelectionPhase.INBOUND
          ? selectedOutbound.details
          : this.getBoundWithCabinClass(outbound, outboundFareFamily),
      inbound: this.getBoundWithCabinClass(inbound, inboundFareFamily),
      availableClasses,
    };

    this.itineraryAndUpgradesModal = {
      open: true,
      data,
    };

    this.gtmService.trackElement(
      `${selectionPhase.toLowerCase()}::view-flight-details`,
      GaContext.FLIGHT_SELECTION,
      ElementTypes.LINK,
      undefined,
      ElementActions.CLICK
    );
  }

  handleBoundOpen(bound: FinnairBoundGroupWithLocation, boundType: BoundType): void {
    this.openBoundClick$.next({ bound, boundType });
  }

  modifySearchSelection(): void {
    this.subscription.add(
      this.languageService.lang
        .pipe(
          take(1),
          map((lang) => `/${lang}`),
          withLatestFrom(this.tripType$)
        )
        .subscribe(([url, tripType]) => {
          if (tripType !== TripType.MULTICITY) {
            this.store$.dispatch(
              PseudoLocalActions.setValue({
                key: GLOBAL_BOOKING_WIDGET_EXPANDER_KEY,
                data: `${GLOBAL_BOOKING_WIDGET_EXPANDER_KEY}-${ExpanderStatus.OPEN}`,
              })
            );
            this.closeModals();
          } else {
            this.router.navigateByUrl(url);
          }
        })
    );
  }

  triggerLogin(): void {
    this.closeModals();
    this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
  }

  private triggerCreateCart(airBoundsIds: string[]): void {
    const boundsHashValue: string = snapshot(this.store$.pipe(boundsHash()));
    const locale = this.languageService.localeValue;
    this.bookingQueueService.queueCartCreateByBounds(airBoundsIds, locale, boundsHashValue);
  }

  private getAvailableClasses(bound: FinnairBoundItemWithLocation, fareFamilies: FinnairBoundFareFamily[]) {
    return bound.itinerary.filter(isFlight).reduce((acc, flight) => {
      acc[flight.id] = fareFamilies.reduce((classes, a) => {
        const { cabinClass } = a.fareInformation.find(({ segmentId }) => segmentId === flight.id);
        if (!classes.includes(cabinClass)) {
          classes.push(cabinClass);
        }
        return classes;
      }, []);
      return acc;
    }, {});
  }

  private mapSpecialOffersStatusToBounds(bounds: FinnairAirBounds): FinnairAirBounds {
    return {
      ...bounds,
      boundGroups: bounds?.boundGroups?.map((bg) => ({
        ...bg,
        hasSpecialOffers: bg.fareFamilies.some((ff) => matchesCampaignFareFamily(ff.fareFamilyCode)),
        fareFamilies: bg.fareFamilies?.map((ff) => ({
          ...ff,
          isSpecialOffer: matchesCampaignFareFamily(ff.fareFamilyCode),
        })),
      })),
    };
  }

  private updateDeeplinkOnFareFamilySelection(boundType: BoundType, fareFamilyCode: string): void {
    if (this.enableNewLanguageSelector) {
      this.subscription.add(
        combineLatest([
          boundType === BoundType.outbound ? this.selectedOutboundItinerary$ : this.selectedInboundItinerary$,
          this.activatedRoute.queryParams,
        ])
          .pipe(take(1))
          .subscribe(([selectedItinerary, params]) => {
            try {
              const deeplinkParams = JSON.parse(decodeURIComponent(params.json));

              const updatedParams = this.mergeParamsToDeeplink(
                boundType === BoundType.outbound ? 0 : 1,
                deeplinkParams,
                selectedItinerary,
                fareFamilyCode
              );

              const newUrl =
                this.router.url.replace(/\?.+/, '') + '?json=' + encodeURIComponent(JSON.stringify(updatedParams));

              this.pageMetaService.prevPageUrl$.next(newUrl.replace(/^\/[\w-]+\//, '/'));
            } catch (error) {
              this.sentryLogger.warn('Failed to parse deeplink', { params, error });
            }
          })
      );
    }
  }

  private mergeParamsToDeeplink(
    index: number,
    params: FinnairMultiCitySearchInterface,
    selectedItinerary: FinnairItineraryItemFlight[],
    fareFamilyCode: string
  ): FinnairMultiCitySearchInterface {
    return {
      ...params,
      flights: (params.flights || []).map((flight, flightIndex) =>
        flightIndex === index
          ? {
              ...flight,
              flightNumbers: selectedItinerary.map(({ flightNumber }) => flightNumber),
              fareFamily: fareFamilyCode,
            }
          : flight
      ),
    };
  }

  private getBoundWithCabinClass = (
    bound: FinnairBoundItemWithLocation | null,
    fareFamily: FinnairBoundFareFamily | null
  ): FinnairBoundItemWithLocation | null => {
    if (!isPresent(bound)) {
      return null;
    }

    const hasBoundAndDetails = isPresent(bound) && isPresent(fareFamily);

    return hasBoundAndDetails
      ? {
          ...bound,
          itinerary: bound.itinerary.map((flight) => {
            const fareInformation = fareFamily.fareInformation.find(
              (fareInfo) => fareInfo.segmentId === (flight as ItineraryItemFlight)?.id
            );
            return {
              ...flight,
              ...(isPresent(fareInformation) && {
                cabinClass: fareInformation?.cabinClass as FinnairCabinClass,
                bookingClass: fareInformation?.bookingClass,
              }),
            };
          }),
        }
      : bound;
  };
}
